import { DateTime } from 'luxon'
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Nullable } from '@types'

import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { Company, Delegate } from '@modules/search/types'
import { parseJSONSafely } from '@shared/utils'
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import ChatModal from './ChatModal'
import chatApi from './api'
import { Chat, ChatMessage, ChatType, GetChatMessagesResponse, GetChatsResponse, LocalChat, SortChatType } from './types'

const ChatContext = React.createContext({
  openChatModal: (data: { chat?: LocalChat; delegates?: Delegate[]; companyId: Company['id']; chatType?: ChatType }) =>
    undefined as void,
  closeChatModal: () => undefined as void,
  openChatModalByChatId: (chatId: number) => undefined as void,
})

const DEFAULT_MESSAGE_AMOUNT = 5

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient()
  const { user, isAuthenticated } = useAuthContext()
  const [openedChat, setOpenedChat] = useState<Nullable<Chat>>(null)
  const [openedLocalChat, setOpenedLocalChat] = useState<Nullable<LocalChat>>(null)
  const [localChatData, setLocalChatData] = useState<Nullable<LocalChat>>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedChatsSort, setSelectedChatsSort] = useState(SortChatType.updated)
  const { ref: loadMoreMessagesRef, inView: isLoadMoreMessagesInView } = useInView({ threshold: 0.1 })
  const { ref: loadMoreChatsRef, inView: isLoadMoreChatsInView } = useInView()

  const modalStateKey = `chat:${user?.id || ''}`

  useEffect(() => {
    const savedState = sessionStorage.getItem(modalStateKey)

    if (savedState) {
      const parsedState = parseJSONSafely(savedState)
      const { openedChat: chat, openedLocalChat: localChat, localChatData: sLocalChatData, isOpen: stateIsOpen } = parsedState

      setOpenedChat(chat ?? null)
      setOpenedLocalChat(localChat ?? null)
      setLocalChatData(sLocalChatData ?? null)
      setIsOpen(stateIsOpen)
    }
  }, [modalStateKey])

  useEffect(() => {
    sessionStorage.setItem(modalStateKey, JSON.stringify({ openedChat, openedLocalChat, localChatData, isOpen }))
  }, [openedChat, openedLocalChat, localChatData, isOpen])

  const {
    data: { counts } = { counts: { unread: {} as { total: number; byChatId: Record<number, number> } } },
    refetch: refetchCounts,
  } = useQuery({
    queryKey: [chatApi.getUnreadMessagesCount.key],
    queryFn: chatApi.getUnreadMessagesCount.fn,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    if (isOpen) {
      queryClient.invalidateQueries({ queryKey: [chatApi.getChats.key, selectedChatsSort] })

      if (openedChat?.id) {
        queryClient.invalidateQueries({ queryKey: [chatApi.getChatMessages.key, openedChat.id] })
      }
    }
  }, [counts])

  // TODO doing pagination as a hot fix, we need to create some graceful util to abstract such code
  const {
    data: getChatsQueryResponse,
    isInitialLoading: isLoading,
    fetchNextPage: fetchNextChatsPage,
  } = useInfiniteQuery<GetChatsResponse, unknown, GetChatsResponse>({
    queryKey: [chatApi.getChats.key, selectedChatsSort],
    queryFn: props => chatApi.getChats.fn({ sortBy: selectedChatsSort, pageParam: props.pageParam as number }),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line 3d party lib ts issues (no error)
    select: ({ pages: rawPages }): GetChatsResponse => {
      const chats = rawPages?.reduce((acc, i) => [...acc, ...i.chats], [] as Chat[])

      return {
        chats,
        total: rawPages[0].total,
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length < Math.ceil(lastPage.total / 10)) {
        return allPages.length
      }

      return undefined
    },
    placeholderData: keepPreviousData,
    enabled: isOpen && !openedChat,
    initialPageParam: 0,
  })

  const { chats: chatList = [] } = (getChatsQueryResponse ?? {}) as GetChatsResponse

  const { data: getChatMessagesResponse, fetchNextPage: fetchNextMessagesPage } = useInfiniteQuery<
    GetChatMessagesResponse,
    unknown,
    GetChatMessagesResponse
  >({
    queryKey: [chatApi.getChatMessages.key, openedChat?.id],
    queryFn: params =>
      chatApi.getChatMessages.fn({
        chatId: openedChat?.id as Chat['id'],
        size: DEFAULT_MESSAGE_AMOUNT,
        pageParam: params.pageParam as number,
      }),

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line 3d party lib ts issues (no error)
    select: ({ pages: rawPages }): GetChatMessagesResponse => {
      const msgs = rawPages?.reduce((acc, i) => [...acc.reverse(), ...i.messages].reverse(), [] as ChatMessage[])

      return {
        messages: msgs,
        members: rawPages[0].members,
        total: rawPages[0].total,
      }
    },
    enabled: !!openedChat && openedChat.id !== -1,

    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length < Math.ceil(lastPage.total / DEFAULT_MESSAGE_AMOUNT)) {
        return allPages.length
      }

      return undefined
    },
    initialPageParam: 0,
  })

  useEffect(() => {
    if (openedChat && selectedChatsSort === SortChatType.unread && counts.unread?.byChatId?.[openedChat.id]) {
      queryClient.invalidateQueries({ queryKey: [chatApi.getChats.key, selectedChatsSort] })
    }
  }, [getChatMessagesResponse])

  const { members, messages } = (getChatMessagesResponse ?? {}) as GetChatMessagesResponse

  useEffect(() => {
    refetchCounts()
  }, [openedChat])

  useEffect(() => {
    if (isOpen) {
      refetchCounts()
    }
  }, [isOpen])

  useEffect(() => {
    if (isLoadMoreMessagesInView) {
      fetchNextMessagesPage()
    }
  }, [isLoadMoreMessagesInView])

  useEffect(() => {
    if (isLoadMoreChatsInView) {
      fetchNextChatsPage()
    }
  }, [isLoadMoreChatsInView])

  const { mutate: getChatByPersonIds } = useMutation({
    mutationFn: chatApi.fetchChatWhere.fn,
    onSuccess: chat => {
      if (chat) {
        setOpenedChat(chat)
        setOpenedLocalChat(null)
      } else {
        setOpenedChat(null)
        setOpenedLocalChat(localChatData)
      }
    },
  })

  const { mutate: getChatById } = useMutation({
    mutationFn: chatApi.getChatById.fn,
    onSuccess: chat => {
      if (chat) {
        queryClient.invalidateQueries({ queryKey: [chatApi.getChatMessages.key, chat.id] })
        setOpenedChat(chat)
        setOpenedLocalChat(null)
        setLocalChatData(null)
        setIsOpen(true)
      }
    },
  })

  const openChatModal = useCallback(
    (
      data: { chat?: LocalChat; delegates?: Delegate[]; companyId: Company['id']; chatType?: ChatType } = {} as {
        companyId: number
      },
    ) => {
      const { delegates, chat, companyId, chatType } = data

      if (chat) {
        getChatByPersonIds({
          delegateIds: chat.withDelegates as number[],
          companyId: chat.type === ChatType.GROUP ? companyId || chat.withCompanyId : undefined,
        })
        setLocalChatData({ ...chat, type: chat.type || (chatType as ChatType), withCompanyId: companyId })
      } else if (delegates?.length) {
        getChatByPersonIds({
          delegateIds: delegates?.map(i => i.id),
          companyId: chatType === ChatType.GROUP ? companyId : undefined,
        })
        setLocalChatData({
          id: -1,
          name: `${delegates[0]?.firstName} ${delegates[0]?.lastName}`,
          subTitle: delegates[0]?.jobTitle,
          companyName: delegates[0]?.companyName,
          avatar: delegates[0]?.profilePicture,
          type: chatType as ChatType,
          senderId: user?.id as number,
          date: DateTime.now().toISO() || '',
          withDelegates: [delegates[0]?.id as number],
          eventData: delegates[0]?.eventData as Delegate['eventData'],
          withCompanyId: companyId,
        })
      }

      setTimeout(() => setIsOpen(true), 500)
    },
    [user],
  )

  const openChatModalByChatId = useCallback((chatId: number) => {
    getChatById({ chatId })
  }, [])

  const closeChatModal = useCallback(() => setIsOpen(false), [])

  const context = useMemo(
    () => ({ openChatModal, closeChatModal, openChatModalByChatId }),
    [openChatModal, closeChatModal, openChatModalByChatId],
  )

  return (
    <ChatContext.Provider value={context}>
      {children}
      {isAuthenticated && (
        <ChatModal
          handleSortChange={setSelectedChatsSort}
          selectedChatsSort={selectedChatsSort}
          loadMoreMessagesRef={loadMoreMessagesRef}
          loadMoreChatsRef={loadMoreChatsRef}
          openedLocalChat={openedLocalChat}
          openedChat={openedChat}
          setLocalOpenedChat={setOpenedLocalChat}
          setOpenedChat={setOpenedChat}
          messages={messages}
          members={members}
          isLoading={isLoading}
          isOpen={isOpen}
          openChatModal={openChatModal}
          closeChatModal={closeChatModal}
          unreadCounts={counts?.unread}
          chats={chatList}
          openChatModalByChatId={openChatModalByChatId}
        />
      )}
    </ChatContext.Provider>
  )
}

export const useChatContext = () => {
  const chatContext = useContext(ChatContext)

  if (!chatContext) {
    throw new Error('useChatContext must be used within an ChatProvider')
  }

  return chatContext
}
